import { useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Upload, X, Move, Image, Layers } from "lucide-react";
import { cn } from "@/lib/utils";

export interface BackgroundImageState {
  backgroundUrl: string | null;
  backgroundOffsetX: number;
  backgroundOffsetY: number;
  backgroundScale: number;
  overlayOffsetX: number;
  overlayOffsetY: number;
  overlayScale: number;
  overlayOpacity: number;
  dragMode: "background" | "overlay";
}

interface BackgroundImageControlsProps {
  state: BackgroundImageState;
  onChange: (state: BackgroundImageState) => void;
}

const BackgroundImageControls = ({ state, onChange }: BackgroundImageControlsProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = (file: File) => {
    if (!file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file);
    onChange({ ...state, backgroundUrl: url, backgroundOffsetX: 0, backgroundOffsetY: 0, backgroundScale: 1, overlayOffsetX: 0, overlayOffsetY: 0 });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [state, onChange]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleRemove = () => {
    if (state.backgroundUrl) {
      URL.revokeObjectURL(state.backgroundUrl);
    }
    onChange({ ...state, backgroundUrl: null, backgroundOffsetX: 0, backgroundOffsetY: 0, backgroundScale: 1, overlayOffsetX: 0, overlayOffsetY: 0 });
  };

  return (
    <div className="space-y-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      {!state.backgroundUrl ? (
        <div
          className={cn(
            "w-full h-24 border-2 border-dashed rounded-md flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors",
            isDragging
              ? "border-primary bg-primary/10"
              : "border-muted-foreground/30 hover:border-primary/50"
          )}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <Upload className="h-6 w-6 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {isDragging ? "Drop image here" : "Drag & drop or click to upload"}
          </span>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="relative rounded-md overflow-hidden border">
            <img
              src={state.backgroundUrl}
              alt="Background preview"
              className="w-full h-24 object-cover"
            />
            <Button
              variant="destructive"
              size="icon"
              className="absolute top-1 right-1 h-6 w-6"
              onClick={handleRemove}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>

          <div>
            <Label className="text-xs font-medium text-muted-foreground mb-1 block">
              Drag Mode
            </Label>
            <ToggleGroup
              type="single"
              value={state.dragMode}
              onValueChange={(v) => {
                if (v) onChange({ ...state, dragMode: v as "background" | "overlay" });
              }}
              className="justify-start"
            >
              <ToggleGroupItem value="background" aria-label="Move background" className="text-xs gap-1">
                <Image className="h-3 w-3" />
                Background
              </ToggleGroupItem>
              <ToggleGroupItem value="overlay" aria-label="Move overlay" className="text-xs gap-1">
                <Layers className="h-3 w-3" />
                Overlay
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          <div>
            <Label className="text-xs font-medium text-muted-foreground mb-1 block">
              Background Scale: {Math.round(state.backgroundScale * 100)}%
            </Label>
            <Slider
              value={[state.backgroundScale]}
              onValueChange={([v]) => onChange({ ...state, backgroundScale: v })}
              min={0.5}
              max={3}
              step={0.05}
            />
          </div>

          <div>
            <Label className="text-xs font-medium text-muted-foreground mb-1 block">
              Overlay Scale: {Math.round(state.overlayScale * 100)}%
            </Label>
            <Slider
              value={[state.overlayScale]}
              onValueChange={([v]) => onChange({ ...state, overlayScale: v })}
              min={0.3}
              max={2}
              step={0.05}
            />
          </div>

          <div>
            <Label className="text-xs font-medium text-muted-foreground mb-1 block">
              Overlay Opacity: {Math.round(state.overlayOpacity * 100)}%
            </Label>
            <Slider
              value={[state.overlayOpacity]}
              onValueChange={([v]) => onChange({ ...state, overlayOpacity: v })}
              min={0.5}
              max={1}
              step={0.05}
            />
          </div>

          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => onChange({ ...state, backgroundOffsetX: 0, backgroundOffsetY: 0, backgroundScale: 1, overlayOffsetX: 0, overlayOffsetY: 0, overlayScale: 1 })}
          >
            Reset Position & Scale
          </Button>
        </div>
      )}
    </div>
  );
};

export default BackgroundImageControls;